'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import Navbar from '../../../../components/atlas/Navbar';
import Footer from '../../../../components/atlas/Footer';
import styles from '../../auth.module.css';

type SignupFormValues = {
  fullName: string;
  username: string;
  email: string;
  password: string;
};

/** Pagina di registrazione con fullName, username, email e password (toggle visibilità). */
export default function SignupPage() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register, handleSubmit, watch } = useForm<SignupFormValues>();
  const passwordValue = watch('password', '');
  const isButtonDisabled = loading || passwordValue.length < 8;

  const onSubmit = async (values: SignupFormValues) => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        router.replace('/applications');
      } else {
        // catch(() => ({})) — fallback silenzioso se il body non è JSON valido
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? 'Registrazione fallita');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <main className={styles.authPage}>
        <div className={styles.authCard}>
          <h1>Create Account</h1>
          <p className={styles.authSubtitle}>
            Join FAST Computing and start simulating
          </p>

          <form className={styles.authForm} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.formGroup}>
              <label htmlFor='fullName'>Full Name</label>
              <input
                id='fullName'
                type='text'
                placeholder='John Doe'
                required
                {...register('fullName')}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor='username'>Username</label>
              <input
                id='username'
                type='text'
                placeholder='johndoe'
                required
                {...register('username')}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor='email'>Email</label>
              <input
                id='email'
                type='email'
                placeholder='name@example.com'
                required
                {...register('email')}
              />
            </div>

            <div className={styles.formGroup} style={{ position: 'relative' }}>
              <label htmlFor='password'>Password</label>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Create a password'
                required
                {...register('password')}
              />
              <button
                type='button'
                className={styles.passwordToggleBtn}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              >
                {showPassword ? (
                  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94'/>
                    <path d='M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19'/>
                    <line x1='1' y1='1' x2='23' y2='23'/>
                  </svg>
                ) : (
                  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/>
                    <circle cx='12' cy='12' r='3'/>
                  </svg>
                )}
              </button>
            </div>

            {error && <p className={styles.authError}>{error}</p>}

            <button
              type='submit'
              className='btn btn-primary btn-full'
              style={{ marginTop: '28px' }}
              disabled={isButtonDisabled}
            >
              {loading ? 'Registrazione…' : 'Sign Up'}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </>
  );
}
